import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useCurrency } from '../context/CurrencyContext';
import { useFavorites } from '../hooks/useFavorites';
import {
  placeholderImage,
  formOptionDefinitions,
  propertyHasRooms,
  PROPERTY_DOCUMENT_TYPES,
} from '../propertyConfig';
import { groupAmenities } from '../amenitiesConfig';
import { buildPropertyFaqs, CANCELLATION_POLICY_TEXT } from '../propertyUtils';
import { normalizeSlot, getPresetForDays, formatTime12h, formatOneTimeDate } from '../viewingScheduleUtils';
import { API_BASE } from '../config';
import {
  formatPrice,
  getPrimaryImage,
  getPropertyAltText,
  getPropertyArea,
  getPropertyAddress,
  getPropertyState,
  getRelativeTimeAgo,
  isTruthy,
} from '../propertyUtils';
import { extractIdFromSlug, generatePropertySlug, propertyMeta, propertyJsonLd } from '../seoUtils';
import { trackEvent } from '../analyticsLoader';
import SeoHead from './SeoHead';
import {
  AlertCircleIcon,
  ArrowRightIcon,
  BuildingIcon,
  CalendarIcon,
  CheckIcon,
  CopyIcon,
  EditIcon,
  FacebookIcon,
  HeartIcon,
  HomeIcon,
  InfoIcon,
  KeyIcon,
  LinkIcon,
  LinkedInIcon,
  MailIcon,
  MapPinIcon,
  MessageCircleIcon,
  MessengerIcon,
  PhoneIcon,
  WhatsAppIcon,
  ReplyIcon,
  ShareIcon,
  TagIcon,
  ThumbsUpIcon,
  UserIcon,
  UsersIcon,
  XIcon,
} from './IconSet';
import ListingCard from './ListingCard';
import PropertyGalleryStage from './PropertyGalleryStage';
import PropertyStaticMapPreview from './PropertyStaticMapPreview';
import PropertyNearbyPlaces from './PropertyNearbyPlaces';
import PropertyFullscreenGallery from './PropertyFullscreenGallery';
import AdBanner from './AdBanner';
import AdSenseSlot from './AdSenseSlot';
import PropertyReviewForm, { REVIEW_TYPE_OPTIONS } from './PropertyReviewForm';
import ReviewPhotoLightbox from './ReviewPhotoLightbox';
import DueDiligenceChecklist from './DueDiligenceChecklist';
import HelpLink from './HelpLink';
import BookingCalendar from './BookingCalendar';
import {
  Accordion,
  Alert,
  Breadcrumbs,
  Button,
  LoadingSpinner,
  Modal,
  Select,
  Tooltip,
} from './index';

function scoreSimilarity(target, candidate) {
  let score = 0;

  if (getPropertyState(target) && getPropertyState(target) === getPropertyState(candidate)) {
    score += 6;
  }

  const targetListing = target.listing_type || target.type;
  const candidateListing = candidate.listing_type || candidate.type;
  if (targetListing && targetListing === candidateListing) {
    score += 4;
  }

  if (target.propertyType && target.propertyType === candidate.propertyType) {
    score += 3;
  }

  const targetPrice = Number(target.price || 0);
  const candidatePrice = Number(candidate.price || 0);

  if (targetPrice > 0 && candidatePrice > 0) {
    const delta = Math.abs(candidatePrice - targetPrice) / targetPrice;
    if (delta <= 0.15) {
      score += 4;
    } else if (delta <= 0.3) {
      score += 2;
    } else if (delta <= 0.5) {
      score += 1;
    }
  }

  return score;
}

const CONTENT_ISSUE_PRIORITY_RANK = { urgent: 3, important: 2, normal: 1 };

function humanizeIssueType(issueType) {
  if (!issueType) {return 'Other';}
  return issueType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function getInitials(name) {
  if (!name) {
    return '?';
  }
  return name
    .split(' ')
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
}

function buildGallery(property) {
  const uniqueImages = [
    getPrimaryImage(property, ''),
    ...(Array.isArray(property?.images) ? property.images : []),
  ].filter(Boolean);

  const dedupedImages = [...new Set(uniqueImages)];
  return dedupedImages.length ? dedupedImages : [placeholderImage];
}

function getPublishedText(property) {
  const source = property?.createdAt || property?.created_at || property?.updatedAt;
  const relative = getRelativeTimeAgo(source);
  return relative ? `Published ${relative}` : 'Published recently';
}

// Rent-only lease term, stored as one of these preset codes (the suffix
// letter is the unit - "m" for months, "y" for years) or 'custom', which
// pairs with a separate lease_duration_months number instead of a label.
const LEASE_DURATION_LABELS = {
  '3m': '3 months',
  '6m': '6 months',
  '12m': '12 months',
  '36m': '36 months',
  '60m': '60+ months',
  '0.25y': '3 months',
  '0.5y': '6 months',
  '1y': '1 year',
  '3y': '3 years',
  '5y': '5+ years',
};

function getLeaseDurationLabel(property) {
  const code = property?.lease_duration || property?.leaseDuration;
  const months = property?.lease_duration_months || property?.leaseDurationMonths;
  if (!code) {
    return 'Not specified';
  }
  if (code === 'custom') {
    return months ? `${months} month(s)` : 'Custom';
  }
  if (LEASE_DURATION_LABELS[code]) {
    return LEASE_DURATION_LABELS[code];
  }
  // Older records can store a bare unit word ("months"/"years") in
  // lease_duration with the actual count in lease_duration_months, rather
  // than one of the coded presets above - still worth combining into a
  // real label instead of falling back to the raw, numberless unit word.
  if (months && /^(months?|years?)$/i.test(code)) {
    return `${months} ${code}`;
  }
  return code;
}

function PropertyDetailsPage({ properties = [], loading, error }) {
  const { slug } = useParams();
  const propertyId = extractIdFromSlug(slug);
  const navigate = useNavigate();
  const location = useLocation();

  const property = useMemo(
    () => properties.find((item) => String(item.id) === String(propertyId)),
    [properties, propertyId],
  );

  const galleryImages = useMemo(() => buildGallery(property), [property]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isFullscreenGalleryOpen, setIsFullscreenGalleryOpen] = useState(false);
  const [fullscreenGalleryTab, setFullscreenGalleryTab] = useState('images');

  const openFullscreenGallery = (tab) => {
    setFullscreenGalleryTab(tab);
    setIsFullscreenGalleryOpen(true);
  };

  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [showAllDetails, setShowAllDetails] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isProtectionTipsModalOpen, setIsProtectionTipsModalOpen] = useState(false);
  const [reportReason, setReportReason] = useState('wrong_info');
  const [reportDetails, setReportDetails] = useState('');
  const [reportStatus, setReportStatus] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [shareStatus, setShareStatus] = useState('');
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const shareMenuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (shareMenuRef.current && !shareMenuRef.current.contains(e.target)) {
        setIsShareMenuOpen(false);
      }
    }
    if (isShareMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isShareMenuOpen]);
  const [contactInfo, setContactInfo] = useState(null);
  const [contactLoading, setContactLoading] = useState(false);
  const [propertyReviews, setPropertyReviews] = useState([]);
  const [propertyReviewsLoading, setPropertyReviewsLoading] = useState(false);
  const [propertyReviewsError, setPropertyReviewsError] = useState('');
  const [showPropertyReviewModal, setShowPropertyReviewModal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const REVIEWS_PAGE_SIZE = 2;
  const [visibleReviewCount, setVisibleReviewCount] = useState(REVIEWS_PAGE_SIZE);
  const [reviewResponseDrafts, setReviewResponseDrafts] = useState({});
  const [reviewResponseSubmittingId, setReviewResponseSubmittingId] = useState(null);
  const [respondingReviewId, setRespondingReviewId] = useState(null);
  const [helpfulPendingReviewId, setHelpfulPendingReviewId] = useState(null);
  const [reportingReview, setReportingReview] = useState(null);
  const [reviewReportReason, setReviewReportReason] = useState('spam');
  const [reviewReportDetails, setReviewReportDetails] = useState('');
  const [reviewReportStatus, setReviewReportStatus] = useState('');
  const [reviewReportLoading, setReviewReportLoading] = useState(false);
  const { user: currentUser, authedFetch } = useAuth();
  const { showToast } = useToast();
  const currencyCtx = useCurrency();
  const { isFavorite: checkFavorite, toggleFavorite } = useFavorites();
  const isFavorite = property ? checkFavorite(property.id) : false;
  const listingType = property?.listing_type || property?.type || 'rent';
  const isShortStay = listingType === 'short-let' || listingType === 'hotel';
  const mainPrice = Number(property?.price || 0);
  const nightlyPrice = Number(property?.price_per_night || 0);
  const isBuy = listingType === 'buy';
  const furnishedLabel = isTruthy(property?.furnished) ? 'Furnished' : 'Unfurnished';
  const hasRooms = propertyHasRooms(property?.propertyType || property?.property_type);
  // Prefer the poster's account-level business profile (logo + name); fall
  // back to the agency name typed into this specific listing's form, then
  // to the poster's own account name - "Listed by" doesn't require an
  // agency, most sellers are individuals, not companies.
  const agencyDisplayName =
    property?.agent_company_name ||
    property?.agency_name ||
    property?.agencyName ||
    property?.agent_name ||
    property?.agentName ||
    null;
  // operating_territory is free text (e.g. "Lagos, Abuja") so an agent can
  // list more than one area - split into individual tags rather than
  // rendering the raw comma-separated string as one run-on line. Only the
  // first 3 render as visible tags; the rest collapse into a "+N" badge
  // (hover to see the full list) rather than the card growing unbounded.
  const VISIBLE_TERRITORY_COUNT = 3;
  const allOperatingTerritories = (
    property?.agent_operating_territory ||
    getPropertyArea(property) ||
    getPropertyState(property) ||
    'Nigeria'
  )
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
    .slice(0, 10);
  const visibleTerritories = allOperatingTerritories.slice(0, VISIBLE_TERRITORY_COUNT);
  const hiddenTerritories = allOperatingTerritories.slice(VISIBLE_TERRITORY_COUNT);
  const listingStatusLabel = isBuy ? 'For sale' : isShortStay ? 'Short-let' : 'For rent';
  const agentMemberSinceLabel = (() => {
    const raw = property?.agent_member_since || property?.agentMemberSince;
    if (!raw) {
      return null;
    }
    const date = new Date(raw);
    return Number.isNaN(date.getTime())
      ? null
      : date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  })();
  const parsedAmenities = useMemo(() => {
    if (!property?.amenities) {
      return [];
    }
    try {
      const val =
        typeof property.amenities === 'string'
          ? JSON.parse(property.amenities)
          : property.amenities;
      return Array.isArray(val) ? val : [];
    } catch {
      return [];
    }
  }, [property?.amenities]);
  // For Sale only, advertiser-declared, never independently verified by
  // NaijaNest (see the disclaimer rendered alongside this list below).
  const propertyDocumentLabels = useMemo(() => {
    const raw = property?.property_documents;
    let keys;
    try {
      keys = Array.isArray(raw) ? raw : typeof raw === 'string' ? JSON.parse(raw) : [];
    } catch {
      keys = [];
    }
    if (!Array.isArray(keys)) {
      return [];
    }
    return keys
      .map((key) => {
        if (key === 'other') {
          const otherName = (property?.property_documents_other || '').trim();
          return otherName ? { key, label: otherName } : null;
        }
        const match = PROPERTY_DOCUMENT_TYPES.find(([docKey]) => docKey === key);
        return match ? { key, label: match[1] } : null;
      })
      .filter(Boolean);
  }, [property?.property_documents, property?.property_documents_other]);
  // Boolean columns (see formOptionDefinitions in propertyConfig.js), not
  // part of the `amenities` JSON array, shown as an extra "Property
  // Features" group alongside it on short-let listings, mirroring how the
  // list-property form now groups them together too.
  const shortLetPropertyFeatures = useMemo(() => {
    if (!property) {return [];}
    const flags = [
      { dbKey: 'furnished', label: 'Furnished' },
      { dbKey: 'serviced', label: 'Serviced' },
      { dbKey: 'parking', label: 'Parking' },
      { dbKey: 'gated', label: 'Gated / Estate' },
      { dbKey: 'near_expressway', label: 'Near expressway' },
      { dbKey: 'flood_free', label: 'Flood-free area' },
    ];
    return flags
      .filter((f) => isTruthy(property[f.dbKey]))
      .map((f) => ({ key: f.dbKey, label: f.label }));
  }, [property]);
  const openViewingSchedule = useMemo(() => {
    if (!property?.open_viewing_schedule) {
      return [];
    }
    try {
      const val =
        typeof property.open_viewing_schedule === 'string'
          ? JSON.parse(property.open_viewing_schedule)
          : property.open_viewing_schedule;
      return Array.isArray(val) ? val : [];
    } catch {
      return [];
    }
  }, [property?.open_viewing_schedule]);
  const propertyFaqs = useMemo(
    () => buildPropertyFaqs(property, currencyCtx),
    [property, currencyCtx],
  );
  const sellerId =
    property?.posted_by || property?.postedBy || property?.seller_id || property?.sellerId || null;
  const sellerProfilePath = (() => {
    if (!sellerId) {
      return '';
    }
    const params = new URLSearchParams();
    if (property?.id) {
      params.set('propertyId', String(property.id));
    }
    if (property?.title) {
      params.set('propertyTitle', property.title);
    }
    if (property?.price != null) {
      params.set('propertyPrice', String(property.price));
    }
    const query = params.toString();
    return `/seller/${sellerId}${query ? `?${query}` : ''}`;
  })();
  const publishedText = getPublishedText(property);
  const bedroomsValue = property?.bedrooms || 'N/A';
  const bathroomsValue = property?.bathrooms || 'N/A';

  // Fetch contact info when the protection-tips modal opens (auth-gated).
  // Skipped once the listing is no longer 'available' - the server now
  // rejects this for anyone but the owner/admin/active reservation holder,
  // so there's no point attempting a call that will just 403.
  const isListingUnavailable =
    !!property?.availability_status && property.availability_status !== 'available';

  const fetchContact = () => {
    if (!currentUser || !property?.id || contactInfo || isListingUnavailable) {
      return;
    }
    setContactLoading(true);
    authedFetch(`${API_BASE}/properties/${property.id}/contact`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) {
          setContactInfo(data);
        }
      })
      .catch(() => {})
      .finally(() => setContactLoading(false));
  };

  useEffect(() => {
    if (
      isProtectionTipsModalOpen &&
      currentUser &&
      property?.id &&
      !contactInfo &&
      !isListingUnavailable
    ) {
      fetchContact();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProtectionTipsModalOpen, currentUser, property?.id, isListingUnavailable]);

  // Fire-and-forget view log, feeds the "Viewing Recommendation" digest -
  // never blocks render or surfaces an error to the visitor.
  useEffect(() => {
    if (!currentUser || !property?.id) {
      return;
    }
    authedFetch(`${API_BASE}/properties/${property.id}/view`, { method: 'POST' }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, property?.id]);

  // GA4 conversion-funnel event - unlike the view log above, this fires for
  // every visitor (not just logged-in ones), since most organic search
  // traffic is anonymous and this is what measures whether that traffic is
  // actually reaching listings.
  useEffect(() => {
    if (!property?.id) {
      return;
    }
    trackEvent('view_property', {
      property_id: property.id,
      listing_type: property.listing_type || property.type,
      state: property.state,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property?.id]);

  // Reusable, not just the mount effect below - also called after a review
  // is created/edited so photos (uploaded in a separate step, after the
  // review itself exists) actually show up without a manual page reload.
  // Optimistic local state can't know about them ahead of time since the
  // upload only starts once the review's id comes back from the server.
  const loadPropertyReviews = useCallback(async () => {
    if (!property?.id) {
      setPropertyReviews([]);
      return;
    }

    setPropertyReviewsLoading(true);
    setPropertyReviewsError('');

    const readJson = async (response) => {
      try {
        return await response.json();
      } catch {
        return null;
      }
    };

    const fetchReviewList = async (url) => {
      const response = await fetch(url);
      const data = await readJson(response);

      if (!data) {
        throw new Error('Unexpected review response');
      }

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load reviews.');
      }

      return data;
    };

    try {
      const propertyReviewsUrl = `${API_BASE}/properties/${encodeURIComponent(
        property.id,
      )}/reviews?limit=50`;

      let data;

      try {
        data = await fetchReviewList(propertyReviewsUrl);
      } catch (propertyReviewError) {
        if (!sellerId) {
          throw propertyReviewError;
        }

        data = await fetchReviewList(
          `${API_BASE}/sellers/${sellerId}/reviews?propertyId=${encodeURIComponent(
            property.id,
          )}&limit=50`,
        );
      }

      setPropertyReviews(data.reviews || []);
    } catch (error) {
      setPropertyReviewsError(error.message || 'Failed to load reviews.');
    } finally {
      setPropertyReviewsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property?.id, sellerId]);

  useEffect(() => {
    setVisibleReviewCount(REVIEWS_PAGE_SIZE);
    loadPropertyReviews();
  }, [property?.id, loadPropertyReviews]);

  const isOwner = currentUser && sellerId && String(currentUser.id) === String(sellerId);

  // Drives the sticky summary bar - appears once the hero card (title/
  // price/actions) has scrolled out from under the site header.
  const heroCardRef = useRef(null);
  const [isPastHero, setIsPastHero] = useState(false);
  // .site-header is only min-height:60px, not a fixed height - it grows
  // taller when the nav wraps to a second row at some viewport widths.
  // Hardcoding the sticky bar's top offset to 60px meant it rendered
  // hidden behind a taller header on those widths - measure the real
  // rendered height instead, so it always docks directly below it.
  const [headerHeight, setHeaderHeight] = useState(60);

  useEffect(() => {
    const headerNode = document.querySelector('.site-header');
    if (!headerNode) {
      return undefined;
    }
    const updateHeight = () => setHeaderHeight(headerNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateHeight);
      return () => window.removeEventListener('resize', updateHeight);
    }
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(headerNode);
    return () => resizeObserver.disconnect();
  }, []);

  // useLayoutEffect (not useEffect) so this resets before the browser paints.
  // Navigating directly between two property pages (e.g. a "Similar
  // properties" card) doesn't unmount this component - it's the same route,
  // just a new :slug param - so the hero card's DOM node survives and this
  // effect only re-runs because property?.id changed. window.scrollTo(0, 0)
  // for the new page (App.js, keyed on location.pathname) runs in a plain
  // useEffect, which only fires *after* the browser paints - so the very
  // first frame of the new property still has the old page's scroll
  // position. Recomputing isPastHero from live geometry at that instant
  // would read that stale, about-to-be-reset scroll and could paint the bar
  // on for one frame before snapping off. Forcing it to false here instead
  // means every new property starts hidden, exactly as if freshly loaded;
  // the IntersectionObserver below reconciles it a moment later if the page
  // genuinely is scrolled past its hero (e.g. browser back/forward restoring
  // a prior scroll position).
  useLayoutEffect(() => {
    setIsPastHero(false);

    const node = heroCardRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setIsPastHero(!entry.isIntersecting),
      { rootMargin: `-${headerHeight}px 0px 0px 0px` },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [property?.id, headerHeight]);

  const canAddPropertyReview = Boolean(currentUser && !isOwner);
  const moderationStatus = property?.moderationStatus;
  const showModerationBanner = isOwner && moderationStatus && moderationStatus !== 'approved';

  // Owner-only "flagged property" banner - surfaces the active admin
  // Content Issue (if any) directly on the property's own page, not just
  // the dashboard. Never fetched for non-owners/public visitors.
  const [contentIssues, setContentIssues] = useState([]);
  useEffect(() => {
    if (!isOwner || !property?.id) {
      setContentIssues([]);
      return;
    }
    authedFetch(`${API_BASE}/content-issues/mine?contentType=PROPERTY`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) =>
        setContentIssues(
          Array.isArray(data) ? data.filter((i) => String(i.content_id) === String(property.id)) : [],
        ),
      )
      .catch(() => setContentIssues([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner, property?.id]);

  const activeContentIssue = useMemo(() => {
    const active = contentIssues.filter((i) =>
      ['OPEN', 'VIEWED', 'OWNER_UPDATED', 'FINAL_REMOVAL_WARNING'].includes(i.status),
    );
    if (!active.length) {return null;}
    return [...active].sort((a, b) => {
      if (a.status === 'FINAL_REMOVAL_WARNING' && b.status !== 'FINAL_REMOVAL_WARNING') {return -1;}
      if (b.status === 'FINAL_REMOVAL_WARNING' && a.status !== 'FINAL_REMOVAL_WARNING') {return 1;}
      return (
        (CONTENT_ISSUE_PRIORITY_RANK[b.priority] || 0) - (CONTENT_ISSUE_PRIORITY_RANK[a.priority] || 0)
      );
    })[0];
  }, [contentIssues]);
  const otherActiveIssueCount = activeContentIssue
    ? contentIssues.filter(
      (i) =>
        i.id !== activeContentIssue.id &&
          ['OPEN', 'VIEWED', 'OWNER_UPDATED', 'FINAL_REMOVAL_WARNING'].includes(i.status),
    ).length
    : 0;

  // Client-only "not interested right now" dismiss for the banner - never
  // touches content_issues server-side (owners must not be able to
  // manipulate issue state). Keyed on status+priority, not just the issue
  // id, so a later escalation (priority bump or FINAL_REMOVAL_WARNING) is
  // treated as a new, more urgent notice and re-surfaces even if the owner
  // dismissed an earlier, milder state of the same issue.
  const contentIssueDismissKey = activeContentIssue
    ? `dismissed-content-issue-${activeContentIssue.id}-${activeContentIssue.status}-${activeContentIssue.priority}`
    : null;
  const [dismissedContentIssueKey, setDismissedContentIssueKey] = useState(null);
  useEffect(() => {
    if (!contentIssueDismissKey) {return;}
    if (window.localStorage.getItem(contentIssueDismissKey) === '1') {
      setDismissedContentIssueKey(contentIssueDismissKey);
    }
  }, [contentIssueDismissKey]);
  const dismissContentIssueBanner = () => {
    if (!contentIssueDismissKey) {return;}
    window.localStorage.setItem(contentIssueDismissKey, '1');
    setDismissedContentIssueKey(contentIssueDismissKey);
  };
  const showContentIssueBanner =
    activeContentIssue && contentIssueDismissKey !== dismissedContentIssueKey;

  const aboutDescription =
    property?.description ||
    `This ${property?.bedrooms || ''}-bedroom ${String(
      property?.propertyType || 'home',
    ).toLowerCase()} in ${
      getPropertyArea(property) || 'a prime location'
    } offers practical living space for modern city life.`;
  const aboutPreview =
    aboutDescription.length > 170
      ? `${aboutDescription.slice(0, 170).trimEnd()}...`
      : aboutDescription;

  const similarProperties = useMemo(() => {
    if (!property) {
      return [];
    }

    return properties
      .filter((item) => String(item.id) !== String(property.id))
      .map((item) => ({ item, score: scoreSimilarity(property, item) }))
      .sort((left, right) => right.score - left.score)
      .slice(0, 4)
      .map((entry) => entry.item);
  }, [properties, property]);

  // "See all" on the Similar Properties section - the grid above only shows
  // the top 4 scored matches, this points to the same listing type/location
  // on Browse for the fuller set, rather than expanding the grid in place.
  const seeAllSimilarHref = useMemo(() => {
    if (!property) {
      return '/browse';
    }
    const params = new URLSearchParams();
    if (listingType) {
      params.set('type', listingType);
    }
    const state = getPropertyState(property);
    if (state) {
      params.set('q', state);
    }
    const area = getPropertyArea(property);
    if (area) {
      params.set('area', area);
    }
    const query = params.toString();
    return query ? `/browse?${query}` : '/browse';
  }, [property, listingType]);

  // Sidebar SEO/cross-sell block - reuses the same /browse URL params as
  // seeAllSimilarHref above (type/q/area), just scoped to area, then state,
  // then the listing type alone, from most to least specific.
  const relatedSearchLinks = useMemo(() => {
    if (!property) {
      return [];
    }
    const typeLabel =
      listingType === 'buy' ? 'for sale' : listingType === 'short-let' ? 'short-lets' : 'for rent';
    const area = getPropertyArea(property);
    const state = getPropertyState(property);
    const links = [];
    if (area) {
      const params = new URLSearchParams({ type: listingType, area });
      links.push({ label: `Properties ${typeLabel} in ${area}`, href: `/browse?${params.toString()}` });
    }
    if (state) {
      const params = new URLSearchParams({ type: listingType, q: state });
      links.push({ label: `Properties ${typeLabel} in ${state}`, href: `/browse?${params.toString()}` });
    }
    const params = new URLSearchParams({ type: listingType });
    links.push({ label: `All properties ${typeLabel}`, href: `/browse?${params.toString()}` });
    return links;
  }, [property, listingType]);

  const propertyReviewAverage = useMemo(() => {
    if (!propertyReviews.length) {
      return 0;
    }

    return (
      Math.round(
        (propertyReviews.reduce((sum, review) => sum + Number(review.rating || 0), 0) /
          propertyReviews.length) *
          10,
      ) / 10
    );
  }, [propertyReviews]);

  // Flattened across every review, not just one - clicking any review
  // photo opens a single swipeable set of every photo submitted for this
  // property, not just the photos from that one review.
  const allReviewPhotos = useMemo(
    () =>
      propertyReviews.flatMap((review) =>
        (Array.isArray(review.photos) ? review.photos : []).map((photo) => ({
          id: photo.id,
          url: photo.url || photo.thumbnail_url || photo.thumbnailUrl,
        })),
      ),
    [propertyReviews],
  );
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const currentUserOwnReview = useMemo(() => {
    if (!currentUser || !propertyReviews.length) {
      return null;
    }

    return (
      propertyReviews.find((review) => {
        const reviewBuyerId = review.buyer_id != null ? String(review.buyer_id) : '';

        return reviewBuyerId === String(currentUser.id);
      }) || null
    );
  }, [currentUser, propertyReviews]);

  const currentUserHasReviewedProperty = Boolean(currentUserOwnReview);

  const handleOpenPropertyReviewModal = () => {
    if (!currentUser) {
      navigate('/login', {
        state: { from: `${window.location.pathname}${window.location.search || ''}` },
      });
      return;
    }

    if (currentUserHasReviewedProperty) {
      return;
    }

    setShowPropertyReviewModal(true);
  };

  const handleSubmitPropertyReview = async (reviewPayload) => {
    if (!currentUser) {
      navigate('/login', {
        state: { from: `${window.location.pathname}${window.location.search || ''}` },
      });
      return;
    }

    try {
      const response = await authedFetch(`${API_BASE}/sellers/${sellerId}/reviews`, {
        method: 'POST',
        body: JSON.stringify({
          ...reviewPayload,
          property_id: property.id,
          transaction_ref: reviewPayload.transaction_ref || `property:${property.id}`,
        }),
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit review.');
      }

      setPropertyReviews((current) => [
        {
          id: result.id || Date.now(),
          buyer_id: currentUser?.id,
          subject: reviewPayload.subject || 'general',
          title: reviewPayload.title || null,
          rating: reviewPayload.rating,
          comment: reviewPayload.comment,
          author: reviewPayload.is_anonymous
            ? 'Anonymous'
            : currentUser?.name || currentUser?.email || 'You',
          buyer_name: reviewPayload.is_anonymous
            ? 'Anonymous'
            : currentUser?.name || currentUser?.email || 'You',
          property_id: property.id,
          property_title: property.title,
          category_ratings: reviewPayload.category_ratings || {},
          recommendation: Boolean(reviewPayload.recommendation),
          review_type: reviewPayload.review_type || [],
          did_visit: reviewPayload.did_visit ?? null,
          recommendation_choice: reviewPayload.recommendation_choice || null,
          transaction_ref: reviewPayload.transaction_ref || `property:${property.id}`,
          seller_response: null,
          seller_response_at: null,
          created_at: new Date().toISOString(),
          photos: [],
        },
        ...current,
      ]);
      showToast(result.message || 'Review submitted successfully.', 'success');
      return result;
    } catch (error) {
      showToast(error.message || 'Failed to submit review.', 'error');
      throw error;
    }
  };

  const handleUpdatePropertyReview = async (reviewPayload) => {
    if (!currentUser || !editingReview) {
      return;
    }

    try {
      const response = await authedFetch(
        `${API_BASE}/sellers/reviews/${editingReview.id}`,
        { method: 'PATCH', body: JSON.stringify(reviewPayload) },
      );
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to update review.');
      }

      setPropertyReviews((current) =>
        current.map((review) =>
          review.id === editingReview.id
            ? {
              ...review,
              rating: reviewPayload.rating,
              comment: reviewPayload.comment,
              author: reviewPayload.is_anonymous
                ? 'Anonymous'
                : currentUser?.name || currentUser?.email || review.author,
              buyer_name: reviewPayload.is_anonymous
                ? 'Anonymous'
                : currentUser?.name || currentUser?.email || review.buyer_name,
              review_type: reviewPayload.review_type || [],
              did_visit: reviewPayload.did_visit ?? null,
              recommendation_choice: reviewPayload.recommendation_choice || null,
              is_edited: true,
            }
            : review,
        ),
      );
      showToast(result.message || 'Review updated.', 'success');
      return { id: editingReview.id };
    } catch (error) {
      showToast(error.message || 'Failed to update review.', 'error');
      throw error;
    }
  };

  const handleSubmitPropertyReviewResponse = async (reviewId) => {
    const responseText = String(reviewResponseDrafts[reviewId] || '').trim();

    if (!responseText || !isOwner) {
      return;
    }

    setReviewResponseSubmittingId(reviewId);

    try {
      const response = await authedFetch(`${API_BASE}/sellers/${sellerId}/reviews/${reviewId}/response`, {
        method: 'POST',
        body: JSON.stringify({ response: responseText }),
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to save response.');
      }

      setPropertyReviews((current) =>
        current.map((review) =>
          review.id === reviewId
            ? {
              ...review,
              seller_response: result.seller_response,
              seller_response_at: result.seller_response_at,
            }
            : review,
        ),
      );
      setReviewResponseDrafts((current) => ({ ...current, [reviewId]: '' }));
      setRespondingReviewId(null);
    } catch (error) {
      showToast(error.message || 'Failed to save response.', 'error');
    } finally {
      setReviewResponseSubmittingId(null);
    }
  };

  const handleToggleReviewHelpful = async (reviewId) => {
    if (!currentUser || helpfulPendingReviewId) {
      return;
    }
    setHelpfulPendingReviewId(reviewId);
    try {
      const response = await authedFetch(`${API_BASE}/sellers/reviews/${reviewId}/helpful`, {
        method: 'POST',
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to save vote.');
      }
      setPropertyReviews((current) =>
        current.map((review) =>
          review.id === reviewId
            ? {
              ...review,
              helpful_count: result.helpful_count,
              current_user_helpful_voted: result.current_user_helpful_voted,
            }
            : review,
        ),
      );
    } catch (error) {
      showToast(error.message || 'Failed to save vote.', 'error');
    } finally {
      setHelpfulPendingReviewId(null);
    }
  };

  const openReviewReportModal = (review) => {
    setReviewReportStatus('');
    setReviewReportDetails('');
    setReviewReportReason('spam');
    setReportingReview(review);
  };

  const handleSubmitReviewReport = async (event) => {
    event.preventDefault();
    if (!reportingReview) {
      return;
    }
    setReviewReportLoading(true);
    setReviewReportStatus('');
    try {
      const response = await authedFetch(
        `${API_BASE}/sellers/reviews/${reportingReview.id}/report`,
        {
          method: 'POST',
          body: JSON.stringify({
            reason: reviewReportReason,
            description: reviewReportDetails.trim(),
          }),
        },
      );
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit report.');
      }
      setReviewReportStatus('success');
      setReviewReportDetails('');
    } catch (err) {
      showToast(err.message || 'Failed to submit report.', 'error');
    } finally {
      setReviewReportLoading(false);
    }
  };

  // Sale/rent's full feature set (see formOptionDefinitions in
  // propertyConfig.js). Short-let's equivalent flags render separately -
  // see shortLetPropertyFeatures above, folded into featuresFlatContent's
  // amenityGroups below rather than duplicated here.
  const saleRentFeatureItems = useMemo(() => {
    if (!property) {
      return [];
    }
    const toSnakeCase = (key) => key.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);
    const formFlags = formOptionDefinitions
      .filter(([, , listingTypes]) => listingTypes.includes(listingType))
      .filter(([dbKey]) => isTruthy(property[dbKey] ?? property[toSnakeCase(dbKey)]))
      .map(([dbKey, label]) => ({ key: dbKey, label }));
    return isTruthy(property?.isVerified)
      ? [{ key: 'isVerified', label: 'Verified' }, ...formFlags]
      : formFlags;
  }, [property, listingType]);

  // Key/value fields for the Property details table (see .property-details-table
  // in core.css - a 2-column dt/dd grid, matching the reference-site-inspired
  // mockup exactly). "always" are the 6 fields shown up front; "extra" are
  // behind the "Show N more details" toggle below.
  const propertyDetailsFields = useMemo(() => {
    if (!property) {
      return { always: [], extra: [] };
    }
    const always = [
      ['Listed by', agencyDisplayName || 'Property agent'],
      ['Property type', property.propertyType || property.property_type || 'Apartment'],
      ['Listing status', listingStatusLabel],
      ...(hasRooms
        ? [
            ['Bedrooms', property.bedrooms || 'N/A'],
            ['Bathrooms', property.bathrooms || 'N/A'],
          ]
        : []),
    ];
    const sellerTypeField = [
      'Seller type',
      property.seller_type || property.sellerType || 'Not specified',
    ];
    const occupancyField = [
      'Occupancy',
      property.occupancy_status || property.occupancyStatus || 'Not specified',
    ];
    const serviceChargeField = [
      'Service charge',
      property.service_charge || property.serviceCharge
        ? formatPrice(property.service_charge || property.serviceCharge, currencyCtx)
        : 'Not specified',
    ];
    const agencyFeeField = [
      'Agency fee',
      property.agency_fee || property.agencyFee
        ? formatPrice(property.agency_fee || property.agencyFee, currencyCtx)
        : 'Not specified',
    ];
    const legalFeeField = [
      'Legal fee',
      property.legal_fee || property.legalFee
        ? formatPrice(property.legal_fee || property.legalFee, currencyCtx)
        : 'Not specified',
    ];
    const paymentTermsField = [
      'Payment terms',
      property.payment_terms || property.paymentTerms || 'Not specified',
    ];

    let extra;
    if (isShortStay) {
      extra = [
        sellerTypeField,
        ['Max guests', property.max_guests || property.maxGuests || 'Not specified'],
        ['Min nights', property.min_nights || property.minNights || 'Not specified'],
        [
          'Available dates',
          property.availability_start || property.availabilityStart
            ? `${property.availability_start || property.availabilityStart}${
              property.availability_end || property.availabilityEnd
                ? ` - ${property.availability_end || property.availabilityEnd}`
                : '+'
            }`
            : 'Not specified',
        ],
        ['Available from', property.available_from || property.availableFrom || 'Not specified'],
      ];
    } else if (isBuy) {
      // Lease/tenancy terms don't apply to a sale - the buy form never
      // collects available-from, tenancy type, lease duration, agreement
      // duration, or caution fee, so this list only shows what buy
      // actually lets a lister fill in (see usePropertyForm.js's buy
      // branch of buildPayload()).
      extra = [
        sellerTypeField,
        paymentTermsField,
        occupancyField,
        serviceChargeField,
        agencyFeeField,
        legalFeeField,
      ];
    } else {
      extra = [
        sellerTypeField,
        paymentTermsField,
        ['Available from', property.available_from || property.availableFrom || 'Not specified'],
        ['Tenancy type', property.tenancy_type || property.tenancyType || 'Not specified'],
        ['Lease duration', getLeaseDurationLabel(property)],
        [
          'Agreement duration',
          property.agreement_duration_months || property.agreementDurationMonths
            ? `${property.agreement_duration_months || property.agreementDurationMonths} month(s)`
            : 'Not specified',
        ],
        occupancyField,
        serviceChargeField,
        [
          'Caution fee',
          property.caution_fee || property.cautionFee
            ? formatPrice(property.caution_fee || property.cautionFee, currencyCtx)
            : 'Not specified',
        ],
        agencyFeeField,
        legalFeeField,
      ];
    }
    return { always, extra };
  }, [property, agencyDisplayName, listingStatusLabel, isShortStay, isBuy, currencyCtx, hasRooms]);

  // Property details section - the tab this used to be called "Overview".
  // A 2-column key/value table (matches the mockup's .details-table exactly),
  // 6 fields always visible, the rest behind a "Show N more details" toggle -
  // this replaces the separate "Tenancy information" modal entirely, not
  // just visually but as its one and only home for this data now.
  const overviewFlatContent = useMemo(
    () => (
      <div className="property-detail-section">
        <dl className={`property-details-table${showAllDetails ? ' expanded' : ''}`}>
          {propertyDetailsFields.always.map(([label, value]) => (
            <div className="property-details-row" key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
          {propertyDetailsFields.extra.map(([label, value]) => (
            <div className="property-details-row property-details-row--extra" key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
        {propertyDetailsFields.extra.length > 0 && (
          <button
            type="button"
            className="property-detail-more-btn"
            onClick={() => setShowAllDetails((v) => !v)}
          >
            {showAllDetails
              ? 'Show less ↑'
              : `Show ${propertyDetailsFields.extra.length} more details ↓`}
          </button>
        )}
        {!isShortStay && openViewingSchedule.length > 0 && (
          <div className="property-detail-flat-block">
            <p className="property-detail-section-title">Open viewing</p>
            <ul className="property-detail-facts" aria-label="Open viewing schedule">
              {openViewingSchedule.map((rawSlot, index) => {
                const slot = normalizeSlot(rawSlot);
                let when;
                if (slot.mode === 'onetime') {
                  when = formatOneTimeDate(slot.date);
                } else {
                  const preset = getPresetForDays(slot.days);
                  when =
                    preset === 'everyday'
                      ? 'Every day'
                      : preset === 'weekdays'
                        ? 'Weekdays'
                        : preset === 'weekends'
                          ? 'Weekends'
                          : slot.days.map((d) => d.slice(0, 3)).join(', ');
                }
                return (
                  <li className="property-detail-fact-item" key={index}>
                    <span>{when}</span>
                    <strong>
                      {formatTime12h(slot.startTime)} - {formatTime12h(slot.endTime)}
                    </strong>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {isBuy && propertyDocumentLabels.length > 0 && (
          <div className="property-detail-flat-block">
            <p className="property-detail-section-title">Property Documentation</p>
            <p className="subtext">Documents stated by advertiser:</p>
            <ul className="property-checklist" aria-label="Property documentation">
              {propertyDocumentLabels.map((doc) => (
                <li className="property-checklist__item" key={doc.key}>
                  <CheckIcon size={16} />
                  <span>{doc.label}</span>
                </li>
              ))}
            </ul>
            <p className="subtext property-documentation-disclaimer">
              Documentation information is provided by the advertiser and has not been
              independently verified by NaijaNest.
            </p>
          </div>
        )}
      </div>
    ),
    [propertyDetailsFields, showAllDetails, isShortStay, openViewingSchedule, isBuy, propertyDocumentLabels],
  );

  // "About this property" - its own section, matching the mockup (was
  // previously folded into the top of the Features and amenities section,
  // which buried it under a different heading entirely).
  const aboutContent = useMemo(
    () => (
      <div className="property-detail-section">
        <p className="property-description">
          {isDescriptionExpanded ? aboutDescription : aboutPreview}
        </p>
        {aboutDescription.length > 170 && (
          <button
            type="button"
            className="property-about-toggle"
            aria-expanded={isDescriptionExpanded}
            onClick={() => setIsDescriptionExpanded((v) => !v)}
          >
            {isDescriptionExpanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    ),
    [isDescriptionExpanded, aboutDescription, aboutPreview],
  );

  // Grouping logic shared by short-let's consolidated Features and
  // amenities tab below - checkbox amenities grouped by category, plus a
  // trailing "Property Features" group for the boolean flags
  // (furnished/serviced/parking/etc.) short-let listings collect.
  const amenityGroups = useMemo(
    () => [
      ...groupAmenities(parsedAmenities),
      ...(shortLetPropertyFeatures.length > 0
        ? [{ id: 'features', label: 'Property Features', amenities: shortLetPropertyFeatures }]
        : []),
    ],
    [parsedAmenities, shortLetPropertyFeatures],
  );

  // "Furnished" is dropped from the rent/buy list here since
  // .property-feature-inline's Sofa icon already shows it, just above, in
  // the metadata row (short-let's own furnished flag lives inside
  // amenityGroups' "Property Features" group instead, not filtered out,
  // since that group doesn't have an equivalent icon elsewhere).
  //
  // Everything a short-let listing can show - the amenity checklist and
  // its own boolean feature flags - lives in this one tab now, rather than
  // being split across a separate "Features" tab (narrow flag subset) and
  // "Amenities" tab (the full grouped list) that said mostly the same
  // thing in two places.
  const featuresFlatContent = useMemo(() => {
    if (isShortStay) {
      const verified = isTruthy(property?.isVerified);
      if (!verified && amenityGroups.length === 0) {
        return (
          <div className="property-detail-section">
            <p className="subtext">No extra feature tags supplied yet.</p>
          </div>
        );
      }
      return (
        <div className="property-detail-section">
          {verified && (
            <ul className="property-checklist">
              <li className="property-checklist__item">
                <CheckIcon size={16} />
                <span>Verified</span>
              </li>
            </ul>
          )}
          {amenityGroups.map((group) => (
            <div key={group.id} className="amenities-display-group">
              <p className="amenities-display-group__title">{group.label}</p>
              <ul className="property-checklist">
                {group.amenities.map((a) => (
                  <li key={a.key} className="property-checklist__item">
                    <CheckIcon size={16} />
                    <span>{a.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      );
    }
    const items = saleRentFeatureItems.filter((item) => item.label !== 'Furnished');
    return (
      <div className="property-detail-section">
        {items.length > 0 ? (
          <ul className="property-checklist">
            {items.map((item) => (
              <li key={item.key} className="property-checklist__item">
                <CheckIcon size={16} />
                <span>{item.label}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="subtext">No extra feature tags supplied yet.</p>
        )}
      </div>
    );
  }, [isShortStay, property?.isVerified, amenityGroups, saleRentFeatureItems]);

  const breadcrumbItems = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    const originSellerId = params.get('sellerId');
    const originSellerName = params.get('sellerName');

    if (params.get('source') === 'seller-profile' && originSellerId) {
      return [
        { label: 'Home', href: '/' },
        { label: originSellerName || 'Seller Profile', href: `/seller/${originSellerId}` },
        { label: property?.title || 'Property', current: true },
      ];
    }

    if (params.get('source') === 'agent-dashboard') {
      return [
        { label: 'Home', href: '/' },
        { label: 'Agent Dashboard', href: '/agent' },
        { label: property?.title || 'Property', current: true },
      ];
    }

    return [
      { label: 'Home', href: '/' },
      { label: 'Browse', href: '/browse' },
      { label: property?.title || 'Property', current: true },
    ];
  }, [location.search, property?.title]);

  const detailTabs = useMemo(
    () => [
      {
        id: 'about',
        label: 'About this property',
        icon: <InfoIcon size={16} />,
        content: aboutContent,
      },
      {
        id: 'overview',
        label: 'Property details',
        icon: <HomeIcon size={16} />,
        content: overviewFlatContent,
      },
      {
        id: 'features',
        label: 'Features and amenities',
        icon: <TagIcon size={16} />,
        content: featuresFlatContent,
      },
      ...(isShortStay && property?.id && !isOwner
        ? [
          {
            id: 'book',
            label: 'Book',
            icon: <CalendarIcon size={16} />,
            content: (
              <div className="property-detail-section">
                <BookingCalendar propertyId={property.id} isOwner={isOwner} />
              </div>
            ),
          },
        ]
        : []),
      {
        id: 'reviews',
        label: `Reviews (${propertyReviews.length})`,
        icon: '★',
        content: (
          <div className="property-detail-section property-reviews-section">
            <div className="property-reviews-header">
              <div className="property-reviews-meta">
                <p className="subtext">
                  {propertyReviews.length > 0
                    ? `${propertyReviewAverage} average rating from ${
                      propertyReviews.length
                    } review${propertyReviews.length === 1 ? '' : 's'}`
                    : isOwner
                      ? 'No reviews yet for this property.'
                      : 'Be the first to review this property.'}
                </p>
                {isOwner ? (
                  <span className="property-reviews-status-pill property-reviews-status-pill--owner">
                    Your listing
                  </span>
                ) : null}
              </div>
              {canAddPropertyReview && currentUserHasReviewedProperty && !editingReview && !showPropertyReviewModal ? (
                <button
                  type="button"
                  className="text-action text-action--green text-action--icon"
                  onClick={() => setEditingReview(currentUserOwnReview)}
                  aria-label="Edit your review"
                  title="Edit your review"
                >
                  <EditIcon size={16} />
                </button>
              ) : canAddPropertyReview && !currentUserHasReviewedProperty && !showPropertyReviewModal ? (
                <button
                  type="button"
                  className="text-action text-action--green"
                  onClick={handleOpenPropertyReviewModal}
                >
                  + Add Your Review
                </button>
              ) : null}
            </div>

            {(showPropertyReviewModal || editingReview) && (
              <PropertyReviewForm
                key={editingReview?.id ?? 'new'}
                existingReview={editingReview}
                onSubmit={editingReview ? handleUpdatePropertyReview : handleSubmitPropertyReview}
                onReviewsChanged={loadPropertyReviews}
                onEditInstead={(reviewLike) => {
                  setShowPropertyReviewModal(false);
                  setEditingReview(reviewLike);
                }}
                onCancel={() => {
                  setShowPropertyReviewModal(false);
                  setEditingReview(null);
                }}
              />
            )}

            {propertyReviewsLoading ? (
              <p className="subtext">Loading reviews…</p>
            ) : propertyReviewsError ? (
              <Alert type="error">{propertyReviewsError}</Alert>
            ) : (
              <div className="reviews-list">
                {propertyReviews.length > 0 &&
                  propertyReviews.slice(0, visibleReviewCount).map((review) => {
                    const isAnon = Number(review.is_anonymous) === 1;
                    const reviewerName = review.author || review.buyer_name;
                    return (
                      <div key={review.id} className="review-item">
                        <div className="review-header">
                          <div
                            className={`review-avatar ${isAnon ? 'review-avatar--anon' : 'review-avatar--named'}`}
                            aria-hidden="true"
                          >
                            {isAnon ? (
                              <UserIcon size={13} />
                            ) : review.buyer_avatar ? (
                              <img src={review.buyer_avatar} alt="" />
                            ) : (
                              getInitials(reviewerName)
                            )}
                          </div>
                          <div className="review-header-text">
                            <h4 className="reviewer-name">{reviewerName}</h4>
                            <span className="review-date">
                              {review.date ||
                              (review.created_at
                                ? new Date(review.created_at).toLocaleDateString()
                                : 'Recently')}
                              {review.is_edited && (
                                <span className="review-edited-tag" title="This review was edited">
                                  {' '}(edited)
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="review-rating">
                          {[...Array(5)].map((_, i) => (
                            <span key={i} className="review-star">
                              {i < Number(review.rating || 0) ? '⭐' : '☆'}
                            </span>
                          ))}
                        </div>
                        {review.title && <p className="review-title">{review.title}</p>}
                        <p className="review-comment">{review.comment}</p>
                        {Array.isArray(review.review_type) && review.review_type.length > 0 && (
                          <div className="review-type-tags">
                            {review.review_type.map((key) => (
                              <span key={key} className="review-type-tag">
                                {REVIEW_TYPE_OPTIONS.find((option) => option.key === key)?.label ||
                                key}
                              </span>
                            ))}
                          </div>
                        )}
                        {Array.isArray(review.photos) && review.photos.length > 0 && (
                          <div className="review-photo-strip">
                            {review.photos.map((photo) => (
                              <img
                                key={photo.id || photo.url}
                                src={photo.thumbnail_url || photo.thumbnailUrl || photo.url}
                                alt=""
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                  const index = allReviewPhotos.findIndex((p) => p.id === photo.id);
                                  setLightboxIndex(index >= 0 ? index : 0);
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    const index = allReviewPhotos.findIndex(
                                      (p) => p.id === photo.id,
                                    );
                                    setLightboxIndex(index >= 0 ? index : 0);
                                  }
                                }}
                              />
                            ))}
                          </div>
                        )}
                        {review.did_visit === true && (
                          <div className="review-visited-pill">✓ Visited in person</div>
                        )}
                        {review.recommendation_choice && (
                          <div className="review-recommend-line">
                            Would recommend:{' '}
                            <strong>
                              {review.recommendation_choice === 'yes'
                                ? 'Yes'
                                : review.recommendation_choice === 'no'
                                  ? 'No'
                                  : 'Not sure'}
                            </strong>
                          </div>
                        )}
                        <div className="review-actions">
                          <button
                            type="button"
                            className={`review-action-btn${review.current_user_helpful_voted ? ' is-active' : ''}`}
                            onClick={() => handleToggleReviewHelpful(review.id)}
                            disabled={!currentUser || helpfulPendingReviewId === review.id}
                          >
                            <ThumbsUpIcon size={11} />
                            Helpful{review.helpful_count > 0 ? ` · ${review.helpful_count}` : ''}
                          </button>
                          <span className="review-action-divider" />
                          <button
                            type="button"
                            className="review-action-btn review-action-btn--report"
                            onClick={() => openReviewReportModal(review)}
                            disabled={!currentUser}
                          >
                            <AlertCircleIcon size={11} />
                            Report
                          </button>
                          {isOwner && !review.seller_response && (
                            <>
                              <span className="review-action-divider" />
                              <button
                                type="button"
                                className="review-action-btn review-action-btn--respond"
                                onClick={() =>
                                  setRespondingReviewId((current) =>
                                    current === review.id ? null : review.id,
                                  )
                                }
                              >
                                <ReplyIcon size={11} />
                                Respond
                              </button>
                            </>
                          )}
                        </div>
                        {review.seller_response && (
                          <div className="review-response">
                            <div className="review-response-avatar" aria-hidden="true">
                              {getInitials(agencyDisplayName)}
                            </div>
                            <div className="review-response-body">
                              <div className="review-response-head">
                                <strong>Response from {agencyDisplayName || 'the seller'}</strong>
                                {review.seller_response_at && (
                                  <span className="review-response-date">
                                    {new Date(review.seller_response_at).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                              <p className="review-response-text">{review.seller_response}</p>
                            </div>
                          </div>
                        )}
                        {isOwner && !review.seller_response && respondingReviewId === review.id && (
                          <div className="review-response-form">
                            <div className="review-response-avatar" aria-hidden="true">
                              {getInitials(agencyDisplayName)}
                            </div>
                            <div className="review-response-form-body">
                              <p className="review-response-form-label">
                                Respond as {agencyDisplayName || 'the seller'}
                              </p>
                              <textarea
                                rows="2"
                                placeholder="Write a reply to this review…"
                                value={reviewResponseDrafts[review.id] || ''}
                                onChange={(event) =>
                                  setReviewResponseDrafts((current) => ({
                                    ...current,
                                    [review.id]: event.target.value,
                                  }))
                                }
                              />
                              <div className="review-response-form-actions">
                                <button
                                  type="button"
                                  className="rf-btn-ghost"
                                  onClick={() => setRespondingReviewId(null)}
                                  disabled={reviewResponseSubmittingId === review.id}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className="rf-btn-primary"
                                  onClick={() => handleSubmitPropertyReviewResponse(review.id)}
                                  disabled={
                                    reviewResponseSubmittingId === review.id ||
                                  !String(reviewResponseDrafts[review.id] || '').trim()
                                  }
                                >
                                  {reviewResponseSubmittingId === review.id
                                    ? 'Posting…'
                                    : 'Post reply'}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
            {propertyReviews.length > visibleReviewCount && (
              <button
                type="button"
                className="property-detail-more-btn"
                onClick={() => setVisibleReviewCount(propertyReviews.length)}
              >
                Load {propertyReviews.length - visibleReviewCount} more review
                {propertyReviews.length - visibleReviewCount === 1 ? '' : 's'} ↓
              </button>
            )}
          </div>
        ),
      },
      ...(isShortStay
        ? [
          {
            id: 'house-rules',
            label: 'House Rules',
            icon: <KeyIcon size={16} />,
            content: (
              <div className="property-detail-section">
                <p className="property-detail-section-title">Check-in / Check-out</p>
                <ul className="property-detail-facts" aria-label="Check-in and check-out times">
                  <li className="property-detail-fact-item">
                    <span>Check-in</span>
                    <strong>{property?.check_in_time || 'Contact host'}</strong>
                  </li>
                  <li className="property-detail-fact-item">
                    <span>Check-out</span>
                    <strong>{property?.check_out_time || 'Contact host'}</strong>
                  </li>
                </ul>

                <p className="property-detail-section-title" style={{ marginTop: '1rem' }}>
                  Damage deposit
                </p>
                <p className="property-description">
                  {property?.damage_deposit_amount
                    ? `A refundable damage deposit of ${formatPrice(
                      property.damage_deposit_amount,
                      currencyCtx,
                    )} is required.${
                      property?.damage_deposit_notes ? ` ${property.damage_deposit_notes}` : ''
                    }`
                    : 'No damage deposit is required for this property.'}
                </p>

                {property?.house_rules && (
                  <>
                    <p className="property-detail-section-title" style={{ marginTop: '1rem' }}>
                      House rules
                    </p>
                    <p className="property-description">{property.house_rules}</p>
                  </>
                )}

                <p className="property-detail-section-title" style={{ marginTop: '1rem' }}>
                  Cancellation policy
                </p>
                <p className="property-description">{CANCELLATION_POLICY_TEXT}</p>
              </div>
            ),
          },
        ]
        : []),
      ...(isShortStay && propertyFaqs.length > 0
        ? [
          {
            id: 'faqs',
            label: 'FAQs',
            icon: <InfoIcon size={16} />,
            content: (
              <div className="property-detail-section property-faq-accordion">
                <Accordion
                  items={propertyFaqs.map((faq, index) => ({
                    id: `faq-${index}`,
                    title: faq.question,
                    content: <p className="property-description">{faq.answer}</p>,
                  }))}
                />
              </div>
            ),
          },
        ]
        : []),
    ],
    [
      aboutContent,
      overviewFlatContent,
      featuresFlatContent,
      currencyCtx,
      currentUser,
      canAddPropertyReview,
      currentUserHasReviewedProperty,
      currentUserOwnReview,
      editingReview,
      handleOpenPropertyReviewModal,
      allReviewPhotos,
      handleSubmitPropertyReview,
      loadPropertyReviews,
      handleUpdatePropertyReview,
      handleSubmitPropertyReviewResponse,
      isOwner,
      isShortStay,
      property,
      propertyFaqs,
      propertyReviewAverage,
      propertyReviews,
      propertyReviewsError,
      propertyReviewsLoading,
      sellerId,
      reviewResponseDrafts,
      reviewResponseSubmittingId,
      respondingReviewId,
      helpfulPendingReviewId,
      handleToggleReviewHelpful,
      openReviewReportModal,
      agencyDisplayName,
      showPropertyReviewModal,
      visibleReviewCount,
      navigate,
    ],
  );

  const handleReport = async (event) => {
    event.preventDefault();
    if (!property || !currentUser) {
      return;
    }
    setReportLoading(true);
    setReportStatus('');
    try {
      const response = await authedFetch(`${API_BASE}/properties/${property.id}/report`, {
        method: 'POST',
        body: JSON.stringify({
          reason: reportReason,
          description: reportDetails.trim(),
        }),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit report.');
      }
      setReportStatus('success');
      setReportDetails('');
    } catch (err) {
      showToast(err.message || 'Failed to submit report.', 'error');
    } finally {
      setReportLoading(false);
    }
  };

  // Reuses the same /properties/:id/contact endpoint the Due Diligence
  // Checklist modal already fetches contactInfo from, rather than adding a
  // second way to reveal the owner's phone number.
  const handleCallAgent = async () => {
    if (!currentUser) {
      navigate('/login', {
        state: { from: `${window.location.pathname}${window.location.search || ''}` },
      });
      return;
    }
    if (isListingUnavailable) {
      showToast('This listing is no longer available.', 'error');
      return;
    }
    trackEvent('contact_agent_click', { property_id: property.id, method: 'call' });
    let info = contactInfo;
    if (!info) {
      setContactLoading(true);
      try {
        const res = await authedFetch(`${API_BASE}/properties/${property.id}/contact`);
        info = res.ok ? await res.json() : null;
        if (info) {
          setContactInfo(info);
        }
      } catch {
        info = null;
      } finally {
        setContactLoading(false);
      }
    }
    if (info?.ownerPhone) {
      window.location.href = `tel:${info.ownerPhone}`;
    } else {
      showToast('Phone number not available for this listing.', 'error');
    }
  };

  // Same /messages bootstrap flow SellerProfileFull.js's "Message" button
  // uses (sellerId/source/propertyId/propertyTitle/propertyPrice query
  // params) - not a new endpoint.
  const handleMessageSeller = () => {
    if (!currentUser) {
      navigate('/login', {
        state: { from: `${window.location.pathname}${window.location.search || ''}` },
      });
      return;
    }
    if (!sellerId) {
      return;
    }
    trackEvent('contact_agent_click', { property_id: property.id, method: 'message' });
    const params = new URLSearchParams();
    params.set('sellerId', String(sellerId));
    params.set('source', 'property-detail');
    params.set('propertyId', String(property.id));
    if (property.title) {
      params.set('propertyTitle', property.title);
    }
    if (property.price != null) {
      params.set('propertyPrice', String(property.price));
    }
    navigate(`/messages?${params.toString()}`);
  };

  // Always opens the shareMenu dropdown below (named channels + a
  // copy-link row) rather than the native Web Share API sheet - some
  // capable browsers (Safari) support navigator.share(), but using it
  // there would skip this dropdown entirely on exactly the browser most
  // people test in, so it's not used at all here, on any browser.
  const handleShare = () => {
    if (!property) {
      return;
    }
    setIsShareMenuOpen((open) => !open);
  };

  const handleCopyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShareStatus('Link copied to clipboard.');
      setIsShareMenuOpen(false);
    } catch (error) {
      setShareStatus('Could not copy the link. Please try again.');
    }
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = property ? `Check out this listing on NaijaNest: ${property.title}` : '';
  const shareChannels = [
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      Icon: WhatsAppIcon,
      className: 'share-menu__icon--whatsapp',
      href: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
      external: true,
    },
    {
      key: 'facebook',
      label: 'Facebook',
      Icon: FacebookIcon,
      className: 'share-menu__icon--facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      external: true,
    },
    {
      key: 'x',
      label: 'X',
      Icon: XIcon,
      className: 'share-menu__icon--x',
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      external: true,
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      Icon: LinkedInIcon,
      className: 'share-menu__icon--linkedin',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      external: true,
    },
    {
      key: 'messenger',
      label: 'Messenger',
      Icon: MessengerIcon,
      className: 'share-menu__icon--messenger',
      // Mobile deep link only (opens the Messenger app directly) - there's
      // no keyless desktop web fallback for Messenger sharing, so this is
      // a best-effort link that simply does nothing on desktop rather than
      // erroring.
      href: `fb-messenger://share/?link=${encodeURIComponent(shareUrl)}`,
      external: false,
    },
    {
      key: 'email',
      label: 'Email',
      Icon: MailIcon,
      className: 'share-menu__icon--email',
      href: `mailto:?subject=${encodeURIComponent(
        property?.title || 'Property listing',
      )}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`,
      external: false,
    },
    {
      key: 'sms',
      label: 'SMS',
      Icon: MessageCircleIcon,
      className: 'share-menu__icon--sms',
      href: `sms:?body=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
      external: false,
    },
  ];

  const availabilityStatus = property?.availability_status || 'available';
  const isPropertyUnavailable = availabilityStatus !== 'available';

  const availabilityLabels = {
    sold: 'This property has been sold and is no longer available.',
    rented: 'This property has been rented and is no longer available.',
    let: 'This property is currently let.',
    booked: 'This property is currently booked.',
    unavailable: 'This property is currently unavailable.',
  };

  if (loading) {
    return (
      <>
        <Breadcrumbs items={breadcrumbItems} />
        <section className="section">
          <LoadingSpinner size="lg" label="Loading property details..." />
        </section>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Breadcrumbs items={breadcrumbItems} />
        <section className="section">
          <p className="error-text">⚠️ {error}</p>
          <button type="button" className="btn-link" onClick={() => navigate('/browse')}>
            Browse listings
          </button>
        </section>
      </>
    );
  }

  if (!property) {
    return (
      <>
        <SeoHead title="Property not found" description="This listing may have been removed or the link is invalid." noIndex />
        <Breadcrumbs items={breadcrumbItems} />
        <section className="section">
          <h1>Property not found</h1>
          <p className="subtext">This listing may have been removed or the link is invalid.</p>
          <button type="button" className="btn-link" onClick={() => navigate('/browse')}>
            Back to browse
          </button>
        </section>
      </>
    );
  }

  const seo = propertyMeta(property);
  const jsonLd = propertyJsonLd(property);

  // Rendered twice below: once in the sidebar (desktop/tablet position,
  // alongside the main column) and once inline in the main column, right
  // before "Property location" (mobile position, once the sidebar stacks
  // below everything else) - each instance's wrapper toggles visibility
  // via CSS at the same breakpoint the grid collapses at, rather than
  // duplicating the buyer seeing it twice at any given width.
  const agentCardContent = sellerId && (
    <div className="card property-agent-card">
      <p className="section-note">Listed by</p>
      <Link
        to={currentUser ? sellerProfilePath : '/login'}
        state={!currentUser ? { from: sellerProfilePath } : undefined}
        className="property-agent-card__top"
      >
        <div className="property-agent-card__avatar">
          {(agencyDisplayName || 'A').charAt(0).toUpperCase()}
        </div>
        <div>
          <p className="property-agent-card__name">{agencyDisplayName || 'Property agent'}</p>
          <p className="property-agent-card__meta property-agent-card__territory">
            <span aria-hidden="true">📍</span>
            <span className="property-agent-card__territory-label">Operates in</span>
            <span className="property-agent-card__territory-tags">
              {visibleTerritories.map((territory) => (
                <span key={territory} className="property-agent-card__territory-tag">
                  {territory}
                </span>
              ))}
              {hiddenTerritories.length > 0 && (
                <Tooltip content={hiddenTerritories.join(', ')} position="bottom">
                  <span className="property-agent-card__territory-tag property-agent-card__territory-tag--more">
                    +{hiddenTerritories.length}
                  </span>
                </Tooltip>
              )}
            </span>
          </p>
          {agentMemberSinceLabel && (
            <p className="property-agent-card__meta">On NaijaNest since {agentMemberSinceLabel}</p>
          )}
        </div>
      </Link>
      <Link to={sellerProfilePath} className="property-agent-card__link">
        See more listings from this agent &rarr;
      </Link>
    </div>
  );

  return (
    <>
      <SeoHead
        title={seo.fullTitle}
        description={seo.desc}
        url={seo.url}
        image={seo.img}
        noIndex={seo.noIndex}
        jsonLd={jsonLd}
      />
      <Breadcrumbs items={breadcrumbItems} />

      <div
        className={`property-sticky-summary${isPastHero ? ' property-sticky-summary--visible' : ''}`}
        style={{ top: `${headerHeight}px` }}
      >
        <div className="property-sticky-summary__inner">
          <img
            src={getPrimaryImage(property, placeholderImage)}
            alt=""
            className="property-sticky-summary__thumb"
          />
          <div className="property-sticky-summary__text">
            <p className="property-sticky-summary__title">{property.title}</p>
            <p className="property-sticky-summary__price">
              {isShortStay && nightlyPrice > 0
                ? formatPrice(nightlyPrice, currencyCtx)
                : formatPrice(mainPrice, currencyCtx)}
            </p>
          </div>
          <div className="property-sticky-summary__actions">
            <Button
              variant="secondary"
              size="small"
              icon={<HeartIcon size={15} />}
              onClick={() => property && toggleFavorite(property.id)}
            >
              {isFavorite ? 'Saved' : 'Save'}
            </Button>
            <Button variant="secondary" size="small" icon={<PhoneIcon size={15} />} onClick={handleCallAgent}>
              Call agent
            </Button>
            <Button size="small" icon={<MessageCircleIcon size={15} />} onClick={handleMessageSeller}>
              Message
            </Button>
          </div>
        </div>
      </div>

      <section className="section property-detail-page">
        {showContentIssueBanner && (
          <Alert
            type={
              activeContentIssue.status === 'FINAL_REMOVAL_WARNING'
                ? 'error'
                : activeContentIssue.status === 'OWNER_UPDATED'
                  ? 'info'
                  : activeContentIssue.priority === 'urgent'
                    ? 'error'
                    : 'warning'
            }
            className="property-content-issue-banner"
          >
            <button
              type="button"
              className="property-content-issue-banner__dismiss"
              aria-label="Dismiss this notice"
              onClick={dismissContentIssueBanner}
            >
              ✕
            </button>
            {activeContentIssue.status === 'OWNER_UPDATED' ? (
              <>
                <strong>Under review.</strong> Your update has been submitted and is waiting on the
                admin team. Reminders and removal are paused while this is under review.
              </>
            ) : (
              <div className="property-content-issue-banner__body">
                <p className="property-content-issue-banner__title">
                  {activeContentIssue.status === 'FINAL_REMOVAL_WARNING'
                    ? 'Final warning: this listing will be removed'
                    : 'Action needed on this listing'}
                </p>
                <p className="property-content-issue-banner__meta">
                  Issue: {humanizeIssueType(activeContentIssue.issue_type)}
                  {' · '}
                  {activeContentIssue.status === 'FINAL_REMOVAL_WARNING' && activeContentIssue.grace_period_ends_at
                    ? `Removal on ${new Date(activeContentIssue.grace_period_ends_at).toLocaleDateString()}`
                    : `Priority: ${
                      activeContentIssue.priority.charAt(0).toUpperCase() +
                        activeContentIssue.priority.slice(1)
                    }`}
                </p>
                <p className="property-content-issue-banner__feedback">{activeContentIssue.admin_feedback}</p>
                <p className="property-content-issue-banner__required">
                  <strong>Required action:</strong> {activeContentIssue.required_action}
                </p>
                <Link to={`/edit-property/${property.id}`} className="property-content-issue-banner__cta">
                  Review &amp; Update Property →
                </Link>
                {otherActiveIssueCount > 0 && (
                  <p className="property-content-issue-banner__more">
                    +{otherActiveIssueCount} more {otherActiveIssueCount === 1 ? 'issue' : 'issues'} open
                  </p>
                )}
              </div>
            )}
          </Alert>
        )}

        {showModerationBanner && (
          <Alert
            type={moderationStatus === 'rejected' ? 'error' : 'warning'}
            className="property-moderation-banner"
          >
            {moderationStatus === 'pending' && (
              <>
                <strong>Your listing is under review.</strong> It is not yet visible to other users.
                We&rsquo;ll notify you once it&rsquo;s approved.
              </>
            )}
            {moderationStatus === 'rejected' && (
              <>
                <strong>Your listing was rejected.</strong>{' '}
                {property.moderationNote
                  ? `Reason: ${property.moderationNote}`
                  : 'It did not meet our listing guidelines.'}{' '}
                Please contact support or edit and resubmit.
              </>
            )}
          </Alert>
        )}

        {isPropertyUnavailable && (
          <Alert type="error" className="property-availability-banner">
            <strong>
              {availabilityStatus.charAt(0).toUpperCase() + availabilityStatus.slice(1)}.
            </strong>{' '}
            {availabilityLabels[availabilityStatus] || 'This property is no longer available.'}
          </Alert>
        )}

        <div className="property-detail-grid">
          <div className="property-detail-main-col">
            <article className="property-detail-card" ref={heroCardRef}>
              <div className="property-detail-content-stack">
                <div className="hero-badges">
                  <span className="pill pill--category">
                    {property.propertyType || property.property_type || 'Apartment'} &middot;{' '}
                    {listingStatusLabel}
                  </span>
                </div>

                <div className="property-detail-header-row">
                  <div className="property-detail-header-block">
                    <h1 className="property-detail-title-link">{property.title}</h1>
                  </div>
                </div>

                <div className="property-price-row">
                  {isShortStay && nightlyPrice > 0 ? (
                    <p className="price">
                      {formatPrice(nightlyPrice, currencyCtx)}{' '}
                      <span className="price-unit">/ night</span>
                    </p>
                  ) : (
                    <p className="price">
                      {formatPrice(mainPrice, currencyCtx)}
                      {!isBuy && <span className="price-unit"> / year</span>}
                    </p>
                  )}
                </div>

                <div className="property-feature-inline" aria-label="Property highlights">
                  <p>
                    <span aria-hidden="true">📍</span>
                    <span>
                      {getPropertyAddress(property) ||
                        `${getPropertyArea(property) || 'Unknown area'}, ${
                          getPropertyState(property) || 'Nigeria'
                        }`}
                    </span>
                  </p>
                  {hasRooms && (
                    <>
                      <p>
                        <span aria-hidden="true">🛌</span>
                        <span>
                          {bedroomsValue} bedroom
                          {Number(property.bedrooms || 0) === 1 ? '' : 's'}
                        </span>
                      </p>
                      <p>
                        <span aria-hidden="true">🚿</span>
                        <span>
                          {bathroomsValue} bathroom
                          {Number(property.bathrooms || 0) === 1 ? '' : 's'}
                        </span>
                      </p>
                      <p>
                        <span aria-hidden="true">🏠</span> <span>{furnishedLabel}</span>
                      </p>
                    </>
                  )}
                  {/* Mobile-only: desktop already sees property type + max
                      guests in the Property details section's overview
                      block, so surfacing it again here would duplicate it -
                      see .property-feature-inline__item--mobile-only. */}
                  <p className="property-feature-inline__item--mobile-only">
                    <BuildingIcon size={18} />{' '}
                    <span>{property?.propertyType || property?.property_type || 'Apartment'}</span>
                  </p>
                  {isShortStay && (property?.max_guests || property?.maxGuests) && (
                    <p className="property-feature-inline__item--mobile-only">
                      <UsersIcon size={18} />{' '}
                      <span>{property.max_guests || property.maxGuests} guests max</span>
                    </p>
                  )}
                </div>

                {shareStatus && <p className="property-share-status">{shareStatus}</p>}

                {isShortStay && (property?.availability_start || property?.availabilityStart) && (
                  <p className="property-location-line property-location-line--mobile-only">
                    <CalendarIcon size={18} />
                    Available {property.availability_start || property.availabilityStart}
                    {property.availability_end || property.availabilityEnd
                      ? ` – ${property.availability_end || property.availabilityEnd}`
                      : '+'}
                  </p>
                )}

                <div className="property-detail-refrow">
                  <span>
                    <span aria-hidden="true">🛏️</span> {publishedText}
                  </span>
                </div>

                <div className="property-detail-actions">
                  <div className="property-detail-actions__group">
                    <Button
                      variant="secondary"
                      icon={<PhoneIcon size={16} />}
                      onClick={handleCallAgent}
                    >
                      Call agent
                    </Button>
                    <Button icon={<MessageCircleIcon size={16} />} onClick={handleMessageSeller}>
                      Message
                    </Button>
                  </div>
                  <div className="property-detail-actions__group">
                    <Button
                      variant="secondary"
                      icon={<HeartIcon size={16} />}
                      onClick={() => property && toggleFavorite(property.id)}
                    >
                      {isFavorite ? 'Saved' : 'Save'}
                    </Button>
                    <div className="share-menu" ref={shareMenuRef}>
                      <Button
                        variant="secondary"
                        icon={<ShareIcon size={16} />}
                        onClick={handleShare}
                        aria-haspopup="true"
                        aria-expanded={isShareMenuOpen}
                      >
                        Share
                      </Button>
                      {isShareMenuOpen && (
                        <div className="share-menu__dropdown" role="menu">
                          <div className="share-menu__list">
                            {shareChannels.map(({ key, label, Icon, className, href, external }) => (
                              <a
                                key={key}
                                href={href}
                                className="share-menu__item"
                                role="menuitem"
                                target={external ? '_blank' : undefined}
                                rel={external ? 'noopener noreferrer' : undefined}
                                onClick={() => setIsShareMenuOpen(false)}
                              >
                                <span className={`share-menu__icon ${className}`}>
                                  <Icon size={15} />
                                </span>
                                <span className="share-menu__label">{label}</span>
                              </a>
                            ))}
                          </div>
                          <div className="share-menu__divider" />
                          <div className="share-menu__copy-row">
                            <span className="share-menu__copy-input">
                              <LinkIcon size={13} />
                              <span>{shareUrl.replace(/^https?:\/\//, '')}</span>
                            </span>
                            <button
                              type="button"
                              className="share-menu__copy-btn"
                              onClick={handleCopyShareLink}
                            >
                              <CopyIcon size={13} />
                              Copy
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    {currentUser && (
                      <Button
                        variant="secondary"
                        icon={<AlertCircleIcon size={16} />}
                        onClick={() => {
                          setReportStatus('');
                          setIsReportModalOpen(true);
                        }}
                      >
                        Report
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </article>

            <article className="property-gallery-panel">
              <PropertyGalleryStage
                images={galleryImages}
                activeIndex={activeImageIndex}
                onActiveIndexChange={setActiveImageIndex}
                altText={getPropertyAltText(property)}
                onExpand={() => openFullscreenGallery('images')}
                overlayActions={
                  <div
                    className="property-gallery-overlay-actions"
                    role="group"
                    aria-label="Listing actions"
                  >
                    <button
                      type="button"
                      className="property-image-action"
                      aria-label="View on map"
                      onClick={() => openFullscreenGallery('map')}
                    >
                      <MapPinIcon size={14} /> <span>Map</span>
                    </button>
                  </div>
                }
              />
            </article>

            <div className="property-detail-section">
              <div className="property-detail-sections-flat">
                {detailTabs.map((tab) => (
                  <React.Fragment key={tab.id}>
                    <div className="property-detail-sections-flat__section">
                      <h2 className="property-detail-sections-flat__heading">
                        {tab.icon} {tab.label}
                      </h2>
                      {tab.content}
                    </div>
                    {tab.id === 'reviews' && (
                      <Alert
                        type="warning"
                        role="note"
                        aria-live="off"
                        icon={<AlertCircleIcon size={16} />}
                        className="property-disclaimer-banner"
                      >
                        This property listing has been provided by the property owner or their
                        authorised advertiser. NaijaNest provides a marketplace for property
                        advertisements and does not own, inspect, verify, or guarantee the
                        accuracy, completeness, legality, ownership, availability, or condition of
                        any listed property. NaijaNest is not a party to any transaction between
                        users. Prospective buyers and tenants should independently verify all
                        information and carry out appropriate due diligence before entering into
                        any agreement.{' '}
                        <Link to="/terms#disclaimer">Read our full disclaimer &rarr;</Link>
                      </Alert>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="property-agent-card-slot--mobile-only">{agentCardContent}</div>

            <div className="card property-detail-sections-flat__section">
              <h2 className="property-detail-sections-flat__heading">Property location</h2>
              <PropertyStaticMapPreview
                latitude={Number(property?.latitude)}
                longitude={Number(property?.longitude)}
                address={getPropertyAddress(property)}
                onClick={() => openFullscreenGallery('map')}
              />
            </div>

            <PropertyNearbyPlaces propertyId={property?.id} isShortStay={isShortStay} />

            {similarProperties.length > 0 && (
              <div className="property-detail-similar-section">
                <div className="property-detail-similar-section__header">
                  <h2 className="property-detail-similar-section__title">You may also like</h2>
                  <Link to={seeAllSimilarHref} className="property-detail-similar-section__see-all">
                    See all <ArrowRightIcon size={14} />
                  </Link>
                </div>
                <div className="listing-grid">
                  {similarProperties.map((item) => (
                    <ListingCard
                      key={item.id}
                      item={item}
                      onOpenProperty={() => navigate(`/property/${generatePropertySlug(item)}`)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="property-detail-side-col">
            <div className="property-agent-card-slot--desktop-only">{agentCardContent}</div>

            <AdBanner slot="sidebar" smoothScroll={false} />
            <AdSenseSlot
              key={propertyId}
              slotId={process.env.REACT_APP_ADSENSE_PROPERTY_SLOT}
              className="property-detail__adsense"
            />

            {relatedSearchLinks.length > 0 && (
              <div className="card property-related-searches-card">
                <p className="section-note">Related searches</p>
                <ul className="property-related-searches-card__list">
                  {relatedSearchLinks.map((link) => (
                    <li key={link.href}>
                      <Link to={link.href}>{link.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Link to="/list-property" className="card property-advertise-card">
              <span className="property-advertise-card__icon" aria-hidden="true">
                📢
              </span>
              <p className="property-advertise-card__title">Got a property to sell or rent?</p>
              <p className="property-advertise-card__subtitle">
                Reach thousands of buyers and tenants on NaijaNest.
              </p>
              <span className="property-advertise-card__cta">Get started &rarr;</span>
            </Link>
          </div>
        </div>
      </section>


      <PropertyFullscreenGallery
        isOpen={isFullscreenGalleryOpen}
        onClose={() => setIsFullscreenGalleryOpen(false)}
        initialTab={fullscreenGalleryTab}
        property={property}
        images={galleryImages}
        activeIndex={activeImageIndex}
        onActiveIndexChange={setActiveImageIndex}
        altText={getPropertyAltText(property)}
      />

      <ReviewPhotoLightbox
        isOpen={lightboxIndex !== null}
        images={allReviewPhotos.map((photo) => photo.url)}
        activeIndex={lightboxIndex || 0}
        onActiveIndexChange={(updater) =>
          setLightboxIndex((current) =>
            typeof updater === 'function' ? updater(current ?? 0) : updater,
          )
        }
        onClose={() => setLightboxIndex(null)}
      />

      <Modal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        title="Report this listing"
      >
        <HelpLink to="/help?category=safety" className="property-report-help-link">
          Safety &amp; reporting guidance
        </HelpLink>
        {reportStatus === 'success' ? (
          <>
            <Alert type="success">
              Thank you — your report has been submitted and will be reviewed by our team.
            </Alert>
            <div className="property-report-actions">
              <Button variant="secondary" onClick={() => setIsReportModalOpen(false)}>
                Close
              </Button>
            </div>
          </>
        ) : (
          <form onSubmit={handleReport} className="property-report-form">
            <label className="property-report-label" htmlFor="report-reason">
              Reason for reporting
            </label>
            <Select
              id="report-reason"
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              options={[
                { value: 'wrong_info', label: 'Incorrect information' },
                { value: 'fraud', label: 'Suspected scam' },
                { value: 'duplicate', label: 'Duplicate listing' },
                { value: 'misleading', label: 'Misleading listing' },
                { value: 'inappropriate', label: 'Inappropriate content' },
                { value: 'already_taken', label: 'Already sold/rented to someone else' },
                { value: 'other', label: 'Other' },
              ]}
            />
            <label className="property-report-label" htmlFor="report-details">
              Additional details <span className="admin-muted">(optional)</span>
            </label>
            <textarea
              id="report-details"
              className="property-report-textarea"
              rows={4}
              maxLength={500}
              placeholder="Describe the issue…"
              value={reportDetails}
              onChange={(e) => setReportDetails(e.target.value)}
            />
            <div className="property-report-actions">
              <Button type="submit" variant="primary" disabled={reportLoading}>
                {reportLoading ? 'Submitting…' : 'Submit report'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={reportLoading}
                onClick={() => setIsReportModalOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={reportingReview !== null}
        onClose={() => setReportingReview(null)}
        title="Report this review"
      >
        <HelpLink to="/help?category=reviews" className="property-report-help-link">
          Review guidelines
        </HelpLink>
        {reviewReportStatus === 'success' ? (
          <>
            <Alert type="success">
              Thank you — your report has been submitted and will be reviewed by our team.
            </Alert>
            <div className="property-report-actions">
              <Button variant="secondary" onClick={() => setReportingReview(null)}>
                Close
              </Button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmitReviewReport} className="property-report-form">
            <label className="property-report-label" htmlFor="review-report-reason">
              Reason for reporting
            </label>
            <Select
              id="review-report-reason"
              value={reviewReportReason}
              onChange={(e) => setReviewReportReason(e.target.value)}
              options={[
                { value: 'spam', label: 'Spam' },
                { value: 'inappropriate', label: 'Inappropriate content' },
                { value: 'fake', label: 'Fake review' },
                { value: 'off_topic', label: 'Off-topic / not about this property' },
                { value: 'other', label: 'Other' },
              ]}
            />
            <label className="property-report-label" htmlFor="review-report-details">
              Additional details <span className="admin-muted">(optional)</span>
            </label>
            <textarea
              id="review-report-details"
              className="property-report-textarea"
              rows={4}
              maxLength={500}
              placeholder="Describe the issue…"
              value={reviewReportDetails}
              onChange={(e) => setReviewReportDetails(e.target.value)}
            />
            <div className="property-report-actions">
              <Button type="submit" variant="primary" disabled={reviewReportLoading}>
                {reviewReportLoading ? 'Submitting…' : 'Submit report'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={reviewReportLoading}
                onClick={() => setReportingReview(null)}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={isProtectionTipsModalOpen}
        onClose={() => setIsProtectionTipsModalOpen(false)}
        title="Due Diligence Checklist"
        size="lg"
        mobilePresentation="sheet"
      >
        <>
          <DueDiligenceChecklist />

          <div className="buyer-protection-actions">
            {contactLoading ? (
              <span style={{ marginRight: '12px' }}>Loading contact…</span>
            ) : contactInfo?.whatsappLink ? (
              <a
                className="whatsapp-contact-btn"
                href={contactInfo.whatsappLink}
                target="_blank"
                rel="noreferrer"
                style={{ marginRight: '12px', display: 'inline-flex' }}
              >
                <WhatsAppIcon size={18} />I understand — Proceed to WhatsApp
              </a>
            ) : contactInfo?.ownerPhone ? (
              <a
                className="whatsapp-contact-btn"
                href={`https://wa.me/234${String(contactInfo.ownerPhone).replace(/^0/, '')}`}
                target="_blank"
                rel="noreferrer"
                style={{ marginRight: '12px', display: 'inline-flex' }}
              >
                <WhatsAppIcon size={18} />I understand — Proceed to WhatsApp
              </a>
            ) : (
              <span className="subtext" style={{ marginRight: '12px' }}>
                {isListingUnavailable
                  ? 'This listing is no longer available.'
                  : 'No contact info available for this listing.'}
              </span>
            )}
            <Button variant="secondary" onClick={() => setIsProtectionTipsModalOpen(false)}>
              Close
            </Button>
          </div>
        </>
      </Modal>
    </>
  );
}

export default PropertyDetailsPage;

PropertyDetailsPage.propTypes = {
  properties: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  error: PropTypes.string,
};
